import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { Form, FormError } from '../../_forms';
import { slugify } from '../../_helpers';
import {
  AlertService,
  AudienceService,
  CallCenterActionService,
  CampaignProductService,
  CRMCampaignService,
  RegionService,
  ResolveSettingsService,
} from '../../_services';
import {
  AudienceBillingCycleOptions,
  AudienceDropdownSettings,
  AudienceOutcome,
  AudienceOutcomeLabels,
  AudienceProductBatchSize,
  AudienceResolveOutcomes,
  AudienceType,
  CallCenterAction,
  CampaignProduct,
  CreateResolveRulePayload,
  CRMCampaign,
  CRMTypeId,
  DefaultMinBillingCycle,
  getCRMCampaignLabel,
  MultiSelectOption,
  Pager,
  Region,
  RequestRefundTab,
  ResolveAudienceRule,
  ResolveRuleCardTypeOptions,
  ResolveRuleConditionEmptyFormValues,
  ResolveRuleConditionLabels,
  ResolveRuleConditionOrder,
  ResolveRuleConditionType,
  ResolveRuleFulfillmentOptions,
  ResolveRulePaymentTypeOptions,
  ResolveRuleProductsDialogId,
  ResolveRuleThreeDsText,
  ResolveRuleTrafficSourceOptions,
  ResolveRuleVisibleProductChipCount,
  ResolveSettings,
  ResolveSettingsMissingMessage,
} from '../../_models';
import { config } from '../../../config/config';
import {
  buildResolveRuleConditionFields,
  buildResolveRuleConditionFormValue,
  getResolveRuleConditionChips,
  getResolveRuleConditionTypes,
  isResolveRuleConditionIncomplete,
} from './resolve-rule-conditions';

@Component({
  selector: 'app-request-refund-settings',
  templateUrl: './request-refund-settings.component.html',
  styleUrls: ['./request-refund-settings.component.scss'],
})
export class RequestRefundSettingsComponent extends Form implements OnInit {
  tabs = RequestRefundTab;
  activeTab = RequestRefundTab.DisplayRules;
  outcomeLabels = AudienceOutcomeLabels;
  outcomeOptions = AudienceResolveOutcomes;
  conditionTypes = ResolveRuleConditionType;
  conditionLabels = ResolveRuleConditionLabels;
  fulfillmentOptions = ResolveRuleFulfillmentOptions;
  cardTypeOptions = ResolveRuleCardTypeOptions;
  paymentTypeOptions = ResolveRulePaymentTypeOptions;
  trafficSourceOptions = ResolveRuleTrafficSourceOptions;
  billingCycles = AudienceBillingCycleOptions;
  threeDsText = ResolveRuleThreeDsText;
  productsDialogId = ResolveRuleProductsDialogId;
  dropdownSettings = { ...AudienceDropdownSettings, itemsShowLimit: 3 };
  countryOptions: MultiSelectOption<number | string>[] = [];
  stateOptions: MultiSelectOption<number | string>[] = [];
  campaignOptions: MultiSelectOption<number | string>[] = [];
  callCenterActions: CallCenterAction[] = [];
  settings: ResolveSettings;
  isSaving = false;
  displayRules: ResolveAudienceRule[] = [];
  ruleProducts: { [ruleId: string]: CampaignProduct[] } = {};
  ruleChips: { [ruleId: string]: string[] } = {};
  showRuleForm = false;
  editingRuleId: string | number = null;
  isSavingRule = false;
  ruleForm: FormGroup;
  ruleConditions: ResolveRuleConditionType[] = [];
  selectedProducts: CampaignProduct[] = [];
  productPickerSelections: CampaignProduct[] = [];
  isProductPickerOpen = false;
  showAllProducts = false;
  showConditionErrors = false;

  constructor(
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    private formBuilder: FormBuilder,
    private resolveSettingsService: ResolveSettingsService,
    private audienceService: AudienceService,
    private callCenterActionService: CallCenterActionService,
    private campaignProductService: CampaignProductService,
    private crmCampaignService: CRMCampaignService,
    private regionService: RegionService,
    private modalService: NgxSmartModalService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.regionService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe((countries: Region[]) => {
        this.countryOptions = countries.map((country) => ({ id: country.id, text: country.name }));
      });

    this.crmCampaignService
      .list({ page: 1, page_size: config.maxPageSize, 'crm__type!': CRMTypeId.Test })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (page: Pager) => {
          this.campaignOptions = page.results.map((campaign: CRMCampaign) => ({
            id: campaign.id,
            text: getCRMCampaignLabel(campaign),
          }));
          this.refreshRuleChips();
        },
        (error) => {
          this.handleError(error);
        }
      );

    this.callCenterActionService
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (actions: CallCenterAction[]) => {
          this.callCenterActions = actions;
        },
        (error) => {
          this.handleError(error);
        }
      );

    this.resolveSettingsService
      .getAccountDefaultSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        if (settings) {
          this.settings = settings;
          this.displayRules = settings.rules || [];
          this.buildForm(settings);
          this.loadRuleProducts();
          this.refreshRuleChips();
        } else {
          this.alertService.error(ResolveSettingsMissingMessage);
        }
      });
  }

  setTab(tab: RequestRefundTab) {
    this.activeTab = tab;
  }

  deleteRule(rule: ResolveAudienceRule) {
    this.audienceService
      .delete(rule.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.displayRules = this.displayRules.filter((existing) => existing.id !== rule.id);
          this.alertService.success('Rule deleted successfully', true);
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  openNewRuleForm() {
    this.editingRuleId = null;
    this.buildRuleForm({ name: null, outcome: AudienceOutcome.OpenRetentionFlow } as ResolveAudienceRule, []);
  }

  openEditRuleForm(rule: ResolveAudienceRule) {
    this.editingRuleId = rule.id;
    this.buildRuleForm(rule, this.ruleProducts[rule.id] || []);

    // the rule's products may still be loading (or failed to); fetch them so a save can't unlink them unseen
    if (rule.use_product_filter && !this.ruleProducts[rule.id]) {
      this.fetchRuleProducts(rule).subscribe((products) => {
        if (this.editingRuleId === rule.id) {
          this.selectedProducts = [...products];
        }
      });
    }
  }

  cancelRuleForm() {
    this.showRuleForm = false;
    this.editingRuleId = null;
  }

  selectOutcome(outcome: AudienceOutcome) {
    this.ruleForm.get('outcome').patchValue(outcome);
  }

  getConditionCountLabel(rule: ResolveAudienceRule): string {
    const count = (this.ruleChips[rule.id] || []).length;
    let label = `${count} conditions`;

    if (count === 0) {
      label = 'No conditions';
    } else if (count === 1) {
      label = '1 condition';
    }

    return label;
  }

  getConditionTypeChoices(current: ResolveRuleConditionType): ResolveRuleConditionType[] {
    return ResolveRuleConditionOrder.filter((type) => type === current || this.ruleConditions.indexOf(type) === -1);
  }

  canAddCondition(): boolean {
    return this.ruleConditions.length < ResolveRuleConditionOrder.length;
  }

  addCondition() {
    const nextType = ResolveRuleConditionOrder.find((type) => this.ruleConditions.indexOf(type) === -1);

    if (nextType) {
      this.ruleConditions = [...this.ruleConditions, nextType];
    }
  }

  changeConditionType(index: number, type: ResolveRuleConditionType) {
    this.resetConditionValue(this.ruleConditions[index]);
    this.ruleConditions = this.ruleConditions.map((existing, position) => (position === index ? type : existing));
  }

  removeCondition(index: number) {
    this.resetConditionValue(this.ruleConditions[index]);
    this.ruleConditions = this.ruleConditions.filter((_, position) => position !== index);
  }

  isConditionIncomplete(type: ResolveRuleConditionType): boolean {
    return isResolveRuleConditionIncomplete(type, this.ruleForm.value, this.selectedProducts);
  }

  getConditionError(type: ResolveRuleConditionType): string {
    return type === ResolveRuleConditionType.BillingCycle ? "The first cycle can't be after the last one." : 'Choose a value.';
  }

  updateStateOptions() {
    const selected: MultiSelectOption<number | string>[] = this.ruleForm.get('countries').value || [];
    // with no country condition, states from every listed country can be picked
    const countryIds = selected.length ? selected.map((country) => country.id) : this.countryOptions.map((country) => country.id);
    const requests = countryIds.map((countryId) => this.regionService.list(countryId as string));

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe((responses: Region[][]) => {
        this.stateOptions = [].concat(...responses).map((state: Region) => ({ id: state.id, text: state.name }));

        const available = this.stateOptions.map((state) => state.id);
        const states: MultiSelectOption<number | string>[] = this.ruleForm.get('states').value || [];
        this.ruleForm.get('states').setValue(states.filter((state) => available.indexOf(state.id) !== -1));
      });
  }

  get visibleProducts(): CampaignProduct[] {
    return this.showAllProducts ? this.selectedProducts : this.selectedProducts.slice(0, ResolveRuleVisibleProductChipCount);
  }

  get ruleNameErrors(): FormError[] {
    const control = this.ruleForm.get('name');
    return control.touched ? this.getErrors(control) : [];
  }

  get hiddenProductCount(): number {
    return this.selectedProducts.length - this.visibleProducts.length;
  }

  openProductPicker() {
    this.productPickerSelections = [...this.selectedProducts];
    this.isProductPickerOpen = true;
    this.modalService.getModal(ResolveRuleProductsDialogId).open();
  }

  closeProductPicker() {
    this.modalService.getModal(ResolveRuleProductsDialogId).close();
  }

  onProductsSelected(productIds: string[]) {
    this.closeProductPicker();

    if (productIds.length) {
      this.campaignProductService
        .list({ id__in: productIds.join(','), page: 1, page_size: config.maxPageSize })
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (page: Pager) => {
            this.selectedProducts = page.results;
          },
          (error) => {
            this.handleError(error);
          }
        );
    } else {
      this.selectedProducts = [];
    }
  }

  removeProduct(product: CampaignProduct) {
    this.selectedProducts = this.selectedProducts.filter((selected) => selected.id !== product.id);
  }

  saveRule() {
    const conditionsComplete = this.ruleConditions.every((type) => !this.isConditionIncomplete(type));

    if (this.ruleForm.invalid || !this.settings || !conditionsComplete) {
      this.ruleForm.markAllAsTouched();
      this.showConditionErrors = true;
    } else {
      this.isSavingRule = true;
      const value = this.ruleForm.value;
      const conditionFields = buildResolveRuleConditionFields(this.ruleConditions, value, this.selectedProducts);
      const nameCheck = this.isRuleNameChanged(value.name)
        ? this.audienceService.validateName(value.name, AudienceType.Resolve)
        : of(null);

      if (this.editingRuleId) {
        nameCheck
          .pipe(
            switchMap(() =>
              this.audienceService.patch(this.editingRuleId, { name: value.name, outcome: value.outcome, ...conditionFields })
            ),
            takeUntil(this.destroy$)
          )
          .subscribe(
            (response: ResolveAudienceRule) => {
              this.displayRules = this.displayRules.map((rule) => (rule.id === response.id ? response : rule));
              this.saveRuleProducts(response, 'Rule updated successfully');
            },
            (error: HttpErrorResponse) => this.handleRuleSaveError(error)
          );
      } else {
        const payload: CreateResolveRulePayload = {
          name: value.name,
          outcome: value.outcome,
          resolve_settings: this.settings.id as number,
          ...conditionFields,
        };

        nameCheck
          .pipe(
            switchMap(() => this.audienceService.create(payload)),
            takeUntil(this.destroy$)
          )
          .subscribe(
            (response: ResolveAudienceRule) => {
              this.displayRules = [...this.displayRules, response];
              this.ruleProducts[response.id] = [];
              // the rule now exists, so retrying after a failed product save updates it instead of creating a duplicate
              this.editingRuleId = response.id;
              this.saveRuleProducts(response, 'Rule created successfully');
            },
            (error: HttpErrorResponse) => this.handleRuleSaveError(error)
          );
      }
    }
  }

  apply() {
    if (this.settings && this.form.invalid) {
      this.form.markAllAsTouched();
    } else if (this.settings) {
      this.isSaving = true;
      const payload = {
        refund_eligibility_window: this.getControlValue('eligibility_window_days'),
        // the number input yields a JS number; the API's money fields are decimal strings
        max_auto_refund_amount: this.hasMaxAutoRefundAmount() ? String(this.getControlValue('max_auto_refund_amount')) : null,
        call_center_action: this.getControlValue('call_center_action'),
        prevent_duplicate_refunds: this.isBooleanFieldChecked('prevent_duplicate_refunds'),
        max_requests_per_window: this.getControlValue('velocity_limit_count'),
        velocity_window_days: this.getControlValue('velocity_limit_days'),
      };

      this.resolveSettingsService
        .patch(this.settings.id, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: ResolveSettings) => {
            this.settings = response;
            this.buildForm(response);
            this.alertService.success('Request Refund settings saved successfully', true);
            this.isSaving = false;
          },
          (error) => {
            this.handleSubmitError(error, false, true);
            this.isSaving = false;
          }
        );
    }
  }

  private buildForm(settings: ResolveSettings) {
    this.setForm(
      this.formBuilder.group({
        eligibility_window_days: [settings.refund_eligibility_window],
        max_auto_refund_amount: [settings.max_auto_refund_amount],
        call_center_action: [settings.call_center_action],
        prevent_duplicate_refunds: [settings.prevent_duplicate_refunds],
        velocity_limit_count: [settings.max_requests_per_window],
        velocity_limit_days: [settings.velocity_window_days],
      })
    );

    this.form
      .get('max_auto_refund_amount')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateCallCenterActionValidator());
    this.updateCallCenterActionValidator();
  }

  private hasMaxAutoRefundAmount(): boolean {
    const value = this.getControlValue('max_auto_refund_amount');

    return value !== null && value !== undefined && value !== '';
  }

  private updateCallCenterActionValidator() {
    const control = this.form.get('call_center_action');

    control.setValidators(this.hasMaxAutoRefundAmount() ? Validators.required : null);
    control.updateValueAndValidity();
  }

  private buildRuleForm(rule: ResolveAudienceRule, products: CampaignProduct[]) {
    this.ruleForm = this.formBuilder.group({
      name: [rule.name, Validators.required],
      outcome: [rule.outcome, Validators.required],
      fulfillment_type: [null],
      min_billing_cycle: [DefaultMinBillingCycle],
      max_billing_cycle: [null],
      merchant_ids: [null],
      cc_types: [[]],
      payment_types: [[]],
      countries: [[]],
      states: [[]],
      crm_campaigns: [[]],
      traffic_sources: [[]],
    });
    this.ruleForm.get('countries').setValue(buildResolveRuleConditionFormValue(rule, this.getConditionLookups()).countries);
    this.updateStateOptions();
    this.ruleForm.patchValue(buildResolveRuleConditionFormValue(rule, this.getConditionLookups()));
    this.ruleConditions = getResolveRuleConditionTypes(rule);
    this.selectedProducts = [...products];
    this.showAllProducts = false;
    this.showConditionErrors = false;
    this.showRuleForm = true;
  }

  private getConditionLookups() {
    return { countries: this.countryOptions, states: this.stateOptions, campaigns: this.campaignOptions };
  }

  private resetConditionValue(type: ResolveRuleConditionType) {
    this.ruleForm.patchValue(ResolveRuleConditionEmptyFormValues[type]);

    if (type === ResolveRuleConditionType.Country) {
      this.updateStateOptions();
    } else if (type === ResolveRuleConditionType.Product) {
      this.selectedProducts = [];
    }
  }

  // validate_name also matches the rule's own slug, so it only runs when the slug changes
  private isRuleNameChanged(name: string): boolean {
    const savedRule = this.displayRules.find((rule) => rule.id === this.editingRuleId);

    return !savedRule || slugify(savedRule.name) !== slugify(name);
  }

  // handleSubmitError only maps errors onto this.form, so the rule's name error is set on ruleForm here
  private handleRuleSaveError(error: HttpErrorResponse) {
    const nameErrors: string[] = error.status === 400 && error.error ? error.error.name : null;

    if (nameErrors && nameErrors.length) {
      this.ruleForm.get('name').setErrors({ string: nameErrors[0] });
      this.ruleForm.get('name').markAsTouched();
    } else {
      this.handleSubmitError(error, false, true);
    }

    this.isSavingRule = false;
  }

  // Products are linked through their own endpoints once the rule exists, so only the difference is sent.
  private saveRuleProducts(rule: ResolveAudienceRule, successMessage: string) {
    const savedIds = (this.ruleProducts[rule.id] || []).map((product) => String(product.id));
    const keptProducts = this.ruleConditions.indexOf(ResolveRuleConditionType.Product) !== -1 ? this.selectedProducts : [];
    const keptIds = keptProducts.map((product) => String(product.id));
    const addedIds = keptIds.filter((id) => savedIds.indexOf(id) === -1);
    const removedIds = savedIds.filter((id) => keptIds.indexOf(id) === -1);
    const requests: Observable<Object>[] = [
      ...this.batchProductRequests(addedIds, (batch) => this.audienceService.add_campaign_products(rule.id, { campaign_products: batch })),
      ...this.batchProductRequests(removedIds, (batch) =>
        this.audienceService.remove_campaign_products(rule.id, { campaign_products: batch })
      ),
    ];

    (requests.length ? forkJoin(requests) : of(null)).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.ruleProducts[rule.id] = keptProducts;
        this.refreshRuleChips();
        this.showRuleForm = false;
        this.editingRuleId = null;
        this.isSavingRule = false;
        this.alertService.success(successMessage, true);
      },
      (error) => {
        this.refreshRuleChips();
        this.handleSubmitError(error, false, true);
        this.isSavingRule = false;
      }
    );
  }

  private batchProductRequests(ids: string[], request: (batch: string[]) => Observable<Object>): Observable<Object>[] {
    const requests: Observable<Object>[] = [];

    for (let start = 0; start < ids.length; start += AudienceProductBatchSize) {
      requests.push(request(ids.slice(start, start + AudienceProductBatchSize)));
    }

    return requests;
  }

  private loadRuleProducts() {
    this.displayRules.filter((rule) => rule.use_product_filter).forEach((rule) => this.fetchRuleProducts(rule).subscribe());
  }

  private fetchRuleProducts(rule: ResolveAudienceRule): Observable<CampaignProduct[]> {
    return this.campaignProductService.list({ audiences: rule.id, page: 1, page_size: config.maxPageSize }).pipe(
      map((page: Pager) => page.results as CampaignProduct[]),
      tap((products) => {
        this.ruleProducts[rule.id] = products;
        this.refreshRuleChips();
      }),
      catchError((error) => {
        this.handleError(error);
        return EMPTY;
      }),
      takeUntil(this.destroy$)
    );
  }

  private refreshRuleChips() {
    const chips = {};

    this.displayRules.forEach((rule) => {
      chips[rule.id] = getResolveRuleConditionChips(rule, {
        countries: this.countryOptions,
        campaigns: this.campaignOptions,
        products: this.ruleProducts[rule.id] || [],
      });
    });

    this.ruleChips = chips;
  }
}
